/**
 * B40 — capability operations (spec arch §2.3: "capabilities outlive the
 * conversation"; M5 ruling 2026-07-23: admin-first thin slice).
 *
 * A capability is a SAVED composition of §11 operations: plain-language
 * intent beside the exact op steps, a schedule, and THE FROZEN GRANT SET —
 * the authoring principal's scopes captured at authoring, stored on the
 * definition, never re-derived (B41). Note what is NOT here: the client
 * never sends grants (the handler freezes them from the authenticated
 * principal — a request field would be a scope-minting door), and going
 * live is a deliberate second step: capability.activate (B44's approval
 * door, admin-gated, below).
 *
 * Audience: ADMIN-only across the board (§11 deny-by-default: the spec
 * names no other role; B44's admin surface is the only planned caller).
 */
import { z } from "zod";
import { readOp, writeOp, zEpochMs, zId } from "./common.js";

/**
 * B473 (b) — the composer's halt sentence begins with these words, on
 * every wall (model calls, tokens, cost). ONE constant, shared: the
 * composer (packages/mcp-server) MINTS its CompositionHaltedError
 * message from it, and the admin compose screen RECOGNISES a halted
 * attempt by it to arm the fresh-key Start over door instead of a
 * Try again that would replay the spent key. Import it — a retyped
 * sniff literal is exactly the drift this constant exists to forbid.
 */
export const COMPOSE_HALT_MESSAGE_PREFIX = "Composition halted at the ";

/**
 * B464 — the EVENT trigger vocabulary on the wire: the §12 integration
 * currency, verbatim. Contracts is the leaf package, so the list lives
 * here and a parity wall in @transact/handlers pins it byte-for-byte to
 * the domain's INTEGRATION_EVENT_NAMES (the two cannot drift silently).
 */
export const CAPABILITY_EVENT_SIGNALS = [
  "OrderConfirmed",
  "OrderExpired",
  "OrderCancelled",
  "PaymentSucceeded",
  "RefundApplied",
  "EntitlementIssued",
  "EntitlementVoided",
  "EntitlementExpired",
  "RedemptionRecorded",
  "SlotCancelled",
  "AllocationReleased",
  "CapacityLow",
  "PeriodTickDay",
  "PeriodTickMonth",
] as const;

export type CapabilityEventSignal = (typeof CAPABILITY_EVENT_SIGNALS)[number];

/**
 * B465 — each signal's bindable payload fields: what a step's
 * {"$event": "<field>"} may name. Derived from the domain event shapes
 * (payload = the emitted event minus type/tenantId/at; machine events
 * gain sourceType through toIntegrationEnvelope) and pinned against
 * REAL, type-checked envelope constructions in @transact/handlers'
 * parity suite — the table cannot drift silently.
 */
export const CAPABILITY_EVENT_PAYLOAD_FIELDS: Readonly<Record<CapabilityEventSignal, readonly string[]>> = Object.freeze({
  OrderConfirmed: ["orderId", "from", "to", "sourceType"],
  OrderExpired: ["orderId", "from", "to", "sourceType"],
  OrderCancelled: ["orderId", "from", "to", "sourceType"],
  PaymentSucceeded: ["intentId", "orderId", "from", "to", "sourceType"],
  RefundApplied: ["orderId", "from", "to", "sourceType"],
  EntitlementIssued: ["entitlementId", "from", "to", "sourceType"],
  EntitlementVoided: ["entitlementId", "from", "to", "sourceType"],
  EntitlementExpired: ["entitlementId", "from", "to", "sourceType"],
  RedemptionRecorded: ["entitlementId", "from", "to", "sourceType"],
  SlotCancelled: ["slotId", "from", "to", "sourceType"],
  AllocationReleased: ["allocationId", "channelId", "poolId", "returnedUnits", "sourceType"],
  CapacityLow: ["poolId", "availableUnits", "capacity", "thresholdBps"],
  PeriodTickDay: ["day"],
  PeriodTickMonth: ["month"],
});

const zEventSignal = z.enum(CAPABILITY_EVENT_SIGNALS);

/** One composed §11 op step: registry name + param bindings (B42 interprets bindings). */
const zCapabilityStep = z
  .object({ operation: z.string().min(1), params: z.record(z.unknown()) })
  .strict();

const zCapabilityState = z.enum(["DRAFT", "ACTIVE", "PAUSED", "RETIRED"]);

const zCapabilitySummary = z
  .object({
    capabilityId: zId,
    name: z.string().min(1),
    state: zCapabilityState,
    schedule: z.string().min(1).optional(),
    /** B464 — an EVENT-kind definition runs when this §12 signal fires. */
    eventTrigger: zEventSignal.optional(),
    createdAt: zEpochMs,
    updatedAt: zEpochMs,
    /** B403 #17 — 43:743's LAST RAN column: the newest run's moment and
     * status. Absent when the capability has never run — the face draws
     * the honest dash, never a dressed-up column of absences (B406). */
    lastRun: z
      .object({ at: zEpochMs, status: z.enum(["RUNNING", "SUCCEEDED", "FAILED", "HALTED"]) })
      .strict()
      .optional(),
  })
  .strict();

const zCapabilityDetail = z
  .object({
    capabilityId: zId,
    name: z.string().min(1),
    intent: z.string().min(1),
    steps: z.array(zCapabilityStep).min(1),
    schedule: z.string().min(1).optional(),
    eventTrigger: zEventSignal.optional(),
    params: z.record(z.unknown()).optional(),
    /** The frozen grant set — read-only evidence of what runs may do. */
    grants: z.array(z.string().min(1)),
    authorId: zId,
    authorKind: z.string().min(1),
    state: zCapabilityState,
    createdAt: zEpochMs,
    updatedAt: zEpochMs,
  })
  .strict();

const EXAMPLE_STEP = { operation: "report.run", params: { report: "sales", range: { $param: "week" } } };
const EXAMPLE_DETAIL = {
  capabilityId: "cap-1",
  name: "Weekly sales report",
  intent: "Email me a weekly sales report",
  steps: [EXAMPLE_STEP],
  schedule: "rate(7 days)",
  grants: ["ledger:read", "order:read"],
  authorId: "staff-admin",
  authorKind: "STAFF",
  state: "DRAFT",
  createdAt: 1_780_000_000_000,
  updatedAt: 1_780_000_000_000,
};

/**
 * §2.3 capability.create — save the composition in DRAFT. The response
 * echoes the grants the handler froze from the CALLING principal; a step
 * needing a permission the author does not hold refuses (the no-escalation
 * wall at authoring, B41).
 */
export const capabilityCreate = writeOp({
  name: "capability.create",
  permission: "capability:write",
  principals: ["STAFF:ADMIN"],
  gate: "admin",
  fields: {
    name: z.string().min(1).max(200),
    intent: z.string().min(1).max(2000),
    steps: z.array(zCapabilityStep).min(1).max(20),
    schedule: z.string().min(1).max(256).optional(),
    /** B464 — exactly one of schedule/eventTrigger (the domain refuses both). */
    eventTrigger: zEventSignal.optional(),
    params: z.record(z.unknown()).optional(),
  },
  response: z
    .object({ capabilityId: zId, state: z.literal("DRAFT"), grants: z.array(z.string().min(1)) })
    .strict(),
  exampleRequest: {
    name: "Weekly sales report",
    intent: "Email me a weekly sales report",
    steps: [EXAMPLE_STEP],
    schedule: "rate(7 days)",
  },
  exampleResponse: { capabilityId: "cap-1", state: "DRAFT", grants: ["ledger:read", "order:read"] },
});

export const capabilityList = readOp({
  name: "capability.list",
  permission: "capability:read",
  principals: ["STAFF:ADMIN"],
  fields: {},
  response: z.object({ capabilities: z.array(zCapabilitySummary) }).strict(),
  exampleRequest: {},
  exampleResponse: {
    capabilities: [
      {
        capabilityId: "cap-1",
        name: "Weekly sales report",
        state: "DRAFT",
        schedule: "rate(7 days)",
        createdAt: 1_780_000_000_000,
        updatedAt: 1_780_000_000_000,
      },
    ],
  },
});

export const capabilityGet = readOp({
  name: "capability.get",
  permission: "capability:read",
  principals: ["STAFF:ADMIN"],
  fields: { capabilityId: zId },
  response: z.object({ capability: zCapabilityDetail.optional() }).strict(),
  exampleRequest: { capabilityId: "cap-1" },
  exampleResponse: { capability: EXAMPLE_DETAIL },
});

/** §2.3 capability.pause — stop future runs; the definition (and its grants) stand. */
export const capabilityPause = writeOp({
  name: "capability.pause",
  permission: "capability:write",
  principals: ["STAFF:ADMIN"],
  fields: { capabilityId: zId },
  response: z.object({ capabilityId: zId, state: z.literal("PAUSED") }).strict(),
  exampleRequest: { capabilityId: "cap-1" },
  exampleResponse: { capabilityId: "cap-1", state: "PAUSED" },
});

export const capabilityResume = writeOp({
  name: "capability.resume",
  permission: "capability:write",
  principals: ["STAFF:ADMIN"],
  fields: { capabilityId: zId },
  response: z.object({ capabilityId: zId, state: z.literal("ACTIVE") }).strict(),
  exampleRequest: { capabilityId: "cap-1" },
  exampleResponse: { capabilityId: "cap-1", state: "ACTIVE" },
});

/**
 * B44's approval door — the deliberate DRAFT -> ACTIVE step the B40 header
 * reserved. Gate "admin": a human administrator approves a definition into
 * service; machines never pass gates. On stages wired with a scheduler,
 * activation materializes the definition's schedule (B42's parked clause);
 * pause/retire remove it, resume restores it. Fail-closed: a SCHEDULED
 * definition refuses to activate where no scheduler is wired — an ACTIVE
 * capability whose schedule can never fire would be a lie.
 */
export const capabilityActivate = writeOp({
  name: "capability.activate",
  permission: "capability:write",
  principals: ["STAFF:ADMIN"],
  gate: "admin",
  fields: { capabilityId: zId },
  response: z.object({ capabilityId: zId, state: z.literal("ACTIVE") }).strict(),
  exampleRequest: { capabilityId: "cap-1" },
  exampleResponse: { capabilityId: "cap-1", state: "ACTIVE" },
});

/** One-way door: a retired capability never runs again (admin approves). */
export const capabilityRetire = writeOp({
  name: "capability.retire",
  permission: "capability:write",
  principals: ["STAFF:ADMIN"],
  gate: "admin",
  fields: { capabilityId: zId },
  response: z.object({ capabilityId: zId, state: z.literal("RETIRED") }).strict(),
  exampleRequest: { capabilityId: "cap-1" },
  exampleResponse: { capabilityId: "cap-1", state: "RETIRED" },
});

const zRunStep = z
  .object({
    operation: z.string().min(1),
    outcome: z.enum(["OK", "FAILED", "SKIPPED"]),
    at: zEpochMs,
    /** B45/B48: the step's reported AI spend, integer minor units. */
    costMinor: z.number().int().nonnegative().optional(),
  })
  .strict();

const zRunSummary = z
  .object({
    runId: zId,
    status: z.enum(["RUNNING", "SUCCEEDED", "FAILED", "HALTED"]),
    startedAt: zEpochMs,
    finishedAt: zEpochMs.optional(),
    steps: z.array(zRunStep),
    /** B45: present exactly when status is HALTED — why the wall stood. */
    haltReason: z.string().min(1).optional(),
  })
  .strict();

/** §2.3 run.history — the capability's execution ledger, newest first. */
export const capabilityRunHistory = readOp({
  name: "capability.run.history",
  permission: "capability:read",
  principals: ["STAFF:ADMIN"],
  fields: { capabilityId: zId, limit: z.number().int().min(1).max(200).optional() },
  response: z.object({ runs: z.array(zRunSummary) }).strict(),
  exampleRequest: { capabilityId: "cap-1", limit: 20 },
  exampleResponse: {
    runs: [
      {
        runId: "run-1",
        status: "SUCCEEDED",
        startedAt: 1_780_000_000_000,
        finishedAt: 1_780_000_060_000,
        steps: [{ operation: "report.run", outcome: "OK", at: 1_780_000_030_000 }],
      },
    ],
  },
});

const zComposeTrace = z
  .object({
    modelCalls: z.number().int().min(1),
    inputTokens: z.number().int().nonnegative(),
    outputTokens: z.number().int().nonnegative(),
    estimatedCostMinor: z.number().int().nonnegative(),
    // B137, additive: the denomination estimatedCostMinor is priced in.
    // Optional so proposals recorded before the field stay readable — a
    // trace without it renders token counts and claims no price.
    currency: z.string().regex(/^[A-Z]{3}$/).optional(),
  })
  .strict();

/**
 * B44 compose — plan mode over the wire: intent in, frozen PROPOSAL out
 * (sentence beside the exact op steps, B43's composer behind the port).
 * NOTHING is saved — capability.create is the save, activate is the door.
 * A write op deliberately: the model call costs money, so Inv 4 rides the
 * clientKey — a retried compose REPLAYS the recorded proposal, it never
 * re-bills. The trace is the honest cost evidence (B48's vocabulary).
 * Ungated: proposing spends pennies and changes nothing; the activate
 * gate is where a human stands.
 */
export const capabilityCompose = writeOp({
  name: "capability.compose",
  permission: "capability:write",
  principals: ["STAFF:ADMIN"],
  fields: {
    intent: z.string().min(1).max(2000),
    // B463 (c) — the deterministic email default: the signed-in operator's
    // own address (from their verified id token), so "email me…" composes
    // to THIS address instead of an invented one. Advisory plan material
    // only: the review face shows it in the exact steps before approval.
    operatorEmail: z.string().email().max(320).optional(),
  },
  // B463 (b)(i)/(c) — compose answers with a PROPOSAL or an honest
  // REFUSAL (operator words + the composer's questions, B429's rail).
  // Both carry the trace: a declined composition still spent model calls.
  // The union is additive: every proposal recorded before the refusal arm
  // existed still parses as the first branch.
  response: z.union([
    z
      .object({
        proposal: z
          .object({
            name: z.string().min(1),
            sentence: z.string().min(1),
            steps: z.array(zCapabilityStep).min(1),
            params: z.record(z.unknown()).optional(),
            schedule: z.string().min(1).optional(),
            /** B464 — the composer may propose an EVENT trigger instead. */
            eventTrigger: zEventSignal.optional(),
          })
          .strict(),
        trace: zComposeTrace,
      })
      .strict(),
    z
      .object({
        refusal: z
          .object({
            message: z.string().min(1).max(600),
            questions: z.array(z.string().min(1).max(300)).max(5),
          })
          .strict(),
        trace: zComposeTrace,
      })
      .strict(),
  ]),
  exampleRequest: { intent: "Email me a weekly sales report" },
  exampleResponse: {
    proposal: {
      name: "Weekly sales report",
      sentence: "Every week, run the sales report and email it to you.",
      steps: [EXAMPLE_STEP],
      schedule: "rate(7 days)",
    },
    trace: { modelCalls: 1, inputTokens: 11_000, outputTokens: 350, estimatedCostMinor: 2, currency: "USD" },
  },
});

/**
 * B113 — intent.interpret: the command bar's fuzzy leg. The caller sends
 * the TEXT and the VOCABULARY it is willing to show (each row a door its
 * own walls already admit); the model maps text onto rows and the
 * response returns only rows drawn FROM that vocabulary — the model can
 * never mint a door. Write-kind for capability.compose's exact reason:
 * a model call costs money, so Inv 4 rides the clientKey (a retry
 * replays, never re-bills). Advisory only: nothing executes, nothing is
 * saved; surfaces re-wall every candidate through their own
 * visibility/mayCall floor before an operator sees it.
 */
const zIntentCandidate = z
  .object({
    kind: z.enum(["insight", "action", "nav"]),
    section: z.string().min(1).max(60),
    op: z.string().min(1).max(80).optional(),
    label: z.string().min(1).max(120),
  })
  .strict();

/**
 * B113 confirm-before-write (B20a additive): a vocabulary row may DECLARE
 * up to four named argument slots — the caller's contract for what the
 * model may extract from the operator's text. Slot names are lowerCamel
 * identifiers; hints are operator words carried into the prompt.
 */
const zIntentSlot = z
  .object({
    name: z.string().regex(/^[a-z][a-zA-Z0-9]{0,29}$/u),
    hint: z.string().min(1).max(120).optional(),
  })
  .strict();

const zIntentVocabularyRow = zIntentCandidate
  .extend({ slots: z.array(zIntentSlot).min(1).max(4).optional() })
  .strict();

/**
 * A returned candidate may carry extracted arguments — bounded string
 * values under slot-shaped keys (the entry-count cap rides the slots
 * declaration: at most four slots exist to fill; plain record here keeps
 * the schema inside the MCP membrane's translatable subset). ADVISORY ONLY, exactly like the row
 * itself: nothing executes server-side; the surface re-walls every key
 * against its own declared slots and nothing runs without an explicit
 * operator confirmation through the op's own §11 door.
 */
const zIntentArguments = z.record(z.string().regex(/^[a-z][a-zA-Z0-9]{0,29}$/u), z.string().min(1).max(120));

const zIntentProposal = zIntentCandidate.extend({ arguments: zIntentArguments.optional() }).strict();

export const intentInterpret = writeOp({
  name: "intent.interpret",
  permission: "ledger:read",
  principals: ["STAFF:MANAGER", "STAFF:ADMIN"],
  fields: {
    text: z.string().min(1).max(500),
    vocabulary: z.array(zIntentVocabularyRow).min(1).max(100),
  },
  response: z
    .object({
      candidates: z.array(zIntentProposal).max(8),
      trace: zComposeTrace,
    })
    .strict(),
  exampleRequest: {
    text: "why did sales dip last week?",
    vocabulary: [{ kind: "insight", section: "reports", op: "report.run", label: "Sales report" }],
  },
  exampleResponse: {
    candidates: [{ kind: "insight", section: "reports", op: "report.run", label: "Sales report" }],
    trace: { modelCalls: 1, inputTokens: 900, outputTokens: 40, estimatedCostMinor: 1 },
  },
});

/**
 * B638 (a) — agent.run: RUN MODE. The model works the §11 operations for
 * the operator — reads run and feed back (it looks at the rows and decides
 * the next step), writes NEVER run inside the loop: in ASK mode a write
 * comes back as a CONFIRM card (pending: the operation, its arguments, the
 * model's one-sentence account of the change) and the run waits; the
 * operator answers with { runId, decision } and the loop continues. DRY
 * records writes unexecuted. Every operation re-enters the kernel under the
 * CALLER's own principal, so the run can reach exactly what the caller can.
 *
 * One door, three shapes: START carries `intent` (+ `mode`, default ASK);
 * RESUME carries `runId` + `decision`; REPLY (B852) carries `reply: {runId,
 * words}` — the operator's next words continue their own answered run, so
 * the assistant is a conversation, not a shot. The handler refuses a mix.
 * Write-kind for capability.compose's exact reason: model calls cost money,
 * so Inv 4 rides the clientKey (a retried start replays, never re-bills).
 * The response is the run as it now stands — its calls are the evidence.
 */
const zAgentRunCall = z
  .object({
    step: z.number().int().min(1),
    operation: z.string().min(1),
    kind: z.enum(["read", "write"]),
    args: z.record(z.unknown()),
    outcome: z.enum(["OK", "REFUSED", "HELD", "CONFIRMED", "DECLINED", "DRY"]),
    rows: z.number().int().nonnegative().optional(),
    code: z.string().min(1).optional(),
    // B638 — the runtime's arithmetic over the read's full rows
    aggregate: z
      .object({
        fn: z.enum(["count", "sum", "avg", "min", "max"]),
        rows: z.string().min(1).optional(),
        field: z.string().min(1).optional(),
        where: z.record(z.union([z.string(), z.number(), z.boolean()])).optional(),
        value: z.number().nullable(),
        over: z.number().int().nonnegative(),
        of: z.number().int().nonnegative(),
      })
      .strict()
      .optional(),
  })
  .strict();

/**
 * B763 (incident TRA-757/758/759/761) — the doors that spend model calls:
 * each reports a `modelCalls` count (compose/interpret's trace, the agent's
 * spend), and each runs 4-16s BY DESIGN. One list, read by the agent (no
 * loops inside loops) and by the latency alarms (a model call is not a slow
 * handler). Walled in test/model-call-ops.test.ts: it must equal the ops
 * whose response declares `modelCalls`, so a new model door cannot be left
 * out of either reader.
 */
export const MODEL_CALL_OPERATIONS: readonly string[] = Object.freeze(["agent.run", "capability.compose", "intent.interpret"]);

export const agentRun = writeOp({
  name: "agent.run",
  permission: "ledger:read",
  principals: ["STAFF:MANAGER", "STAFF:ADMIN"],
  fields: {
    intent: z.string().min(1).max(2000).optional(),
    mode: z.enum(["ASK", "DRY"]).optional(),
    runId: zId.optional(),
    decision: z.enum(["CONFIRM", "DECLINE"]).optional(),
    // B852 (b) — the operator's next words resume their own run (the third shape)
    reply: z.object({ runId: zId, words: z.string().min(1).max(2000) }).strict().optional(),
  },
  response: z
    .object({
      runId: zId,
      mode: z.enum(["ASK", "DRY"]),
      status: z.enum(["ANSWERED", "AWAITING_CONFIRM", "HALTED", "FAILED"]),
      answer: z
        .object({
          text: z.string().min(1),
          table: z.object({ columns: z.array(z.string()).min(1), rows: z.array(z.array(z.string())) }).strict().optional(),
          // B852 (c) — things to press: chips whose words reply into the run, or one form whose CONTINUE does
          choices: z
            .array(z.object({ label: z.string().min(1).max(40), words: z.string().min(1).max(300) }).strict())
            .min(1)
            .max(6)
            .optional(),
          form: z
            .object({
              fields: z
                .array(
                  z
                    .object({
                      name: z.string().min(1).max(60),
                      kind: z.enum(["text", "money", "percent", "date", "time", "number", "choice"]),
                      choices: z.array(z.string().min(1).max(60)).min(2).max(12).optional(),
                      hint: z.string().min(1).max(120).optional(),
                    })
                    .strict(),
                )
                .min(1)
                .max(8),
            })
            .strict()
            .optional(),
        })
        .strict()
        .optional(),
      pending: z
        .object({ step: z.number().int().min(1), operation: z.string().min(1), args: z.record(z.unknown()), sentence: z.string().min(1) })
        .strict()
        .optional(),
      reason: z.string().min(1).optional(),
      calls: z.array(zAgentRunCall),
      spend: z
        .object({
          modelCalls: z.number().int().nonnegative(),
          inputTokens: z.number().int().nonnegative(),
          outputTokens: z.number().int().nonnegative(),
          estimatedCostMinor: z.number().int().nonnegative(),
          currency: z.string().regex(/^[A-Z]{3}$/).optional(),
        })
        .strict(),
    })
    .strict(),
  exampleRequest: { intent: "How many orders did we take this week?", mode: "ASK" },
  exampleResponse: {
    runId: "run-1",
    mode: "ASK",
    status: "ANSWERED",
    answer: { text: "42 orders so far this week." },
    calls: [{ step: 1, operation: "order.list", kind: "read", args: { limit: 500 }, outcome: "OK", rows: 42 }],
    spend: { modelCalls: 3, inputTokens: 9_000, outputTokens: 300, estimatedCostMinor: 2, currency: "USD" },
  },
});


/* --------------------------------------------------------- B45 guardrails */

/** The resolved snapshot the runtime enforces — defaults filled in, budgets absent = unlimited. */
const zEffectiveGuardrails = z
  .object({
    pauseAll: z.boolean(),
    maxStepsPerRun: z.number().int().min(1),
    maxCostMinorPerRun: z.number().int().min(1).optional(),
    dailyAiBudgetMinor: z.number().int().min(1).optional(),
  })
  .strict();

/**
 * B45 capability.guardrails.get — the circuit-breaker panel: the tenant's
 * EFFECTIVE guardrails (platform defaults where unconfigured), whether a
 * record exists, and today's AI spend — the honest budget evidence.
 */
export const capabilityGuardrailsGet = readOp({
  name: "capability.guardrails.get",
  permission: "capability:read",
  principals: ["STAFF:ADMIN"],
  fields: {},
  response: z
    .object({ guardrails: zEffectiveGuardrails, configured: z.boolean(), spentTodayMinor: z.number().int().nonnegative() })
    .strict(),
  exampleRequest: {},
  exampleResponse: {
    guardrails: { pauseAll: false, maxStepsPerRun: 25, dailyAiBudgetMinor: 500 },
    configured: true,
    spentTodayMinor: 14,
  },
});

/**
 * B45 capability.guardrails.set — configure the breakers: per-run step +
 * spend caps, the per-tenant daily AI budget, and pause_all (the tenant
 * kill switch: no runs fire, no composition spends, until unset). A full-
 * record replace — what you send is what stands; omitted caps fall to the
 * platform defaults. The response echoes the resolved effective snapshot.
 */
export const capabilityGuardrailsSet = writeOp({
  name: "capability.guardrails.set",
  permission: "capability:write",
  principals: ["STAFF:ADMIN"],
  fields: {
    pauseAll: z.boolean(),
    maxStepsPerRun: z.number().int().min(1).max(10_000).optional(),
    maxCostMinorPerRun: z.number().int().min(1).max(100_000_000).optional(),
    dailyAiBudgetMinor: z.number().int().min(1).max(100_000_000).optional(),
  },
  response: z.object({ guardrails: zEffectiveGuardrails }).strict(),
  exampleRequest: { pauseAll: false, maxStepsPerRun: 10, dailyAiBudgetMinor: 500 },
  exampleResponse: { guardrails: { pauseAll: false, maxStepsPerRun: 10, dailyAiBudgetMinor: 500 } },
});

/* ------------------------------------------------------ B48 flight trace */

const zFlightModelCall = z
  .object({
    kind: z.literal("MODEL_CALL"),
    capabilityId: zId.optional(),
    context: z.enum(["compose", "run", "interpret", "agent"]),
    contextId: z.string().min(1),
    seq: z.number().int().nonnegative(),
    at: zEpochMs,
    inputTokens: z.number().int().nonnegative(),
    outputTokens: z.number().int().nonnegative(),
    costMinor: z.number().int().nonnegative(),
    stopReason: z.string().min(1),
    toolCallName: z.string().min(1).optional(),
    /** B471 reopen — WHY this call's output was refused, when it was. */
    corrective: z.string().min(1).optional(),
    model: z.string().min(1).optional(),
  })
  .strict();

const zFlightToolInvocation = z
  .object({
    kind: z.literal("TOOL_INVOCATION"),
    capabilityId: zId,
    context: z.literal("run"),
    contextId: z.string().min(1),
    seq: z.number().int().nonnegative(),
    at: zEpochMs,
    operation: z.string().min(1),
    outcome: z.enum(["OK", "FAILED", "SKIPPED"]),
    durationMs: z.number().int().nonnegative(),
    costMinor: z.number().int().nonnegative().optional(),
  })
  .strict();

/**
 * B48 capability.flight.trace — the recorder's read: every Bedrock call +
 * tool invocation of one capability (or one context: a compose clientKey or
 * a runId), with the totalCostMinor rollup B44's history display and the
 * cost report read. "If you can't explain it, you can't trust it."
 */
export const capabilityFlightTrace = readOp({
  name: "capability.flight.trace",
  permission: "capability:read",
  principals: ["STAFF:ADMIN"],
  fields: { capabilityId: zId.optional(), contextId: z.string().min(1).optional() },
  response: z
    .object({
      records: z.array(z.discriminatedUnion("kind", [zFlightModelCall, zFlightToolInvocation])),
      totalCostMinor: z.number().int().nonnegative(),
    })
    .strict(),
  exampleRequest: { capabilityId: "cap-1" },
  exampleResponse: {
    records: [
      {
        kind: "TOOL_INVOCATION",
        capabilityId: "cap-1",
        context: "run",
        contextId: "run-1",
        seq: 0,
        at: 1_780_000_000_000,
        operation: "report.run",
        outcome: "OK",
        durationMs: 40,
        costMinor: 3,
      },
    ],
    totalCostMinor: 3,
  },
});

/* ------------------------------------------------- B460 ask conversations */

/**
 * B460 (b) — the /ask conversation's wire: every exchange the answer
 * page settles is APPENDED to a per-staff thread, snapshotting WHAT WAS
 * SHOWN (headline, figures, series, table, or the refusal's words), so
 * history REPLAYS byte-for-byte and never silently re-queries under an
 * old question. PER-STAFF BY CONSTRUCTION: no staffId travels — the
 * handler keys the store on the AUTHENTICATED principal's own id (the
 * B98 staff-prefs stance). Audience and scope are the ask bar's own:
 * ledger:read, MANAGER/ADMIN (the intent.interpret precedent — a
 * dedicated scope would be an authorizer amendment, deliberately not
 * taken). Append rides Inv 4's ordinary clientKey, so a retried write
 * is one exchange, not two.
 */
const zAskShownFigure = z.object({ label: z.string().min(1).max(160), value: z.string().min(1).max(200) }).strict();
const zAskShownPoint = z.object({ label: z.string().min(1).max(160), value: z.number() }).strict(); // finiteness is the domain door's wall — .finite() is outside the membrane's translatable subset
const zAskShownTable = z
  .object({
    columns: z
      .array(
        z
          .object({
            key: z.string().min(1).max(160),
            header: z.string().min(1).max(160),
            align: z.enum(["amount", "num"]).optional(),
            register: z.enum(["strong", "muted", "product"]).optional(),
            priority: z.number().int().optional(),
          })
          .strict(),
      )
      .min(1)
      .max(12),
    rows: z.array(z.object({ key: z.string().min(1).max(160), cells: z.record(z.string(), z.string().max(200)) }).strict()).max(200),
  })
  .strict();

const zAskShownAnswer = z.discriminatedUnion("kind", [
  z
    .object({
      kind: z.literal("answer"),
      headline: z.string().min(1).max(600),
      figures: z.array(zAskShownFigure).max(12),
      source: z.string().min(1).max(160),
      note: z.string().min(1).max(600).optional(),
      series: z.array(zAskShownPoint).max(400).optional(),
      seriesFormat: z.enum(["money", "count"]).optional(),
      table: zAskShownTable.optional(),
    })
    .strict(),
  z.object({ kind: z.literal("refusal"), message: z.string().min(1).max(600), source: z.string().min(1).max(160) }).strict(),
]);

const zAskExchangeInput = z
  .object({
    typed: z.string().min(1).max(500),
    shown: zAskShownAnswer,
    how: z.array(z.string().min(1).max(300)).max(8),
  })
  .strict();

const zAskExchangeRecord = z
  .object({
    typed: z.string().min(1).max(500),
    shown: zAskShownAnswer,
    how: z.array(z.string().min(1).max(300)).max(8),
    askedAt: zEpochMs,
  })
  .strict();

const zAskThreadSummary = z
  .object({
    threadId: zId,
    firstQuestion: z.string().min(1).max(500),
    exchanges: z.number().int().positive(),
    createdAt: zEpochMs,
    updatedAt: zEpochMs,
  })
  .strict();

export const askThreadAppend = writeOp({
  name: "ask.thread.append",
  permission: "ledger:read",
  principals: ["STAFF:MANAGER", "STAFF:ADMIN"],
  fields: { threadId: zId, exchange: zAskExchangeInput },
  response: z.object({ threadId: zId, exchanges: z.number().int().positive(), updatedAt: zEpochMs }).strict(),
  exampleRequest: {
    threadId: "thread-1",
    exchange: {
      typed: "sales today",
      shown: { kind: "answer", headline: "Sales today", figures: [{ label: "Net", value: "$120.00" }], source: "report.run · sales · today (UTC)" },
      how: ["“sales today” matched Sales report"],
    },
  },
  exampleResponse: { threadId: "thread-1", exchanges: 1, updatedAt: 1_780_000_000_000 },
});

export const askThreadList = readOp({
  name: "ask.thread.list",
  permission: "ledger:read",
  principals: ["STAFF:MANAGER", "STAFF:ADMIN"],
  fields: {},
  response: z.object({ threads: z.array(zAskThreadSummary).max(50) }).strict(),
  exampleRequest: {},
  exampleResponse: {
    threads: [{ threadId: "thread-1", firstQuestion: "sales today", exchanges: 2, createdAt: 1_780_000_000_000, updatedAt: 1_780_000_060_000 }],
  },
});

export const askThreadGet = readOp({
  name: "ask.thread.get",
  permission: "ledger:read",
  principals: ["STAFF:MANAGER", "STAFF:ADMIN"],
  fields: { threadId: zId },
  response: z
    .object({ threadId: zId, createdAt: zEpochMs, updatedAt: zEpochMs, exchanges: z.array(zAskExchangeRecord).min(1).max(100) })
    .strict(),
  exampleRequest: { threadId: "thread-1" },
  exampleResponse: {
    threadId: "thread-1",
    createdAt: 1_780_000_000_000,
    updatedAt: 1_780_000_000_000,
    exchanges: [
      {
        typed: "sales today",
        shown: { kind: "answer", headline: "Sales today", figures: [{ label: "Net", value: "$120.00" }], source: "report.run · sales · today (UTC)" },
        how: ["“sales today” matched Sales report"],
        askedAt: 1_780_000_000_000,
      },
    ],
  },
});
